import { openDB } from 'idb';

/**
 * Offline support for field work.
 *
 * Roofers lose signal in attics, behind chimneys and on half the rural jobs in
 * Michigan. Two halves:
 *
 *   reads   GET responses are mirrored into IndexedDB, so the app opens and is
 *           browsable with no connection instead of showing an error page.
 *
 *   writes  Mutations that fail *at the network level* are queued and replayed
 *           on reconnect, each carrying a stable Idempotency-Key so a retry
 *           cannot create the record twice (see src/idempotency.js).
 *
 * An HTTP error is NOT a network failure. A 400 means the server rejected the
 * payload and replaying it would fail identically for ever, so only genuine
 * connection failures queue.
 */

const DB_NAME = '5river-offline';
const DB_VERSION = 1;
const CACHE = 'cache';
const QUEUE = 'queue';

const dbp = openDB(DB_NAME, DB_VERSION, {
  upgrade(db) {
    if (!db.objectStoreNames.contains(CACHE)) db.createObjectStore(CACHE);
    if (!db.objectStoreNames.contains(QUEUE)) {
      db.createObjectStore(QUEUE, { keyPath: 'id', autoIncrement: true });
    }
  },
  // If another tab is holding an older connection open, the browser blocks
  // this one indefinitely — openDB() never resolves or rejects. That's not
  // just hypothetical: it's exactly what made the whole app boot hang with
  // no error, because AppContext's boot sequence awaits a cache read on any
  // network hiccup. Logging here at least makes it diagnosable; the timeout
  // below is what actually keeps the app from freezing.
  blocked() { console.warn('[offline] IndexedDB upgrade blocked by another open tab.'); },
});

const DB_TIMEOUT_MS = 4000;

/** Never let a stuck IndexedDB connection hang a caller forever. */
function withTimeout(promise, ms = DB_TIMEOUT_MS) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('IndexedDB timed out')), ms)),
  ]);
}

// ------------------------------------------------------------------- reads

export async function cachePut(path, data) {
  try {
    const db = await withTimeout(dbp);
    await db.put(CACHE, { data, at: Date.now() }, path);
  } catch { /* a full, blocked or stuck store must never break a working request */ }
}

export async function cacheGet(path) {
  try {
    const db = await withTimeout(dbp);
    return (await db.get(CACHE, path)) || null;
  } catch { return null; }
}

// ------------------------------------------------------------------ writes

/**
 * Only these are safe to replay unattended.
 *
 * Deliberately excluded: anything that emails a customer (/send), records a
 * payment, or signs an agreement. Those have consequences outside the app —
 * an invoice silently emailed an hour late, from a queue the owner forgot
 * about, is worse than being told to retry while they can still think about it.
 */
const QUEUEABLE = [
  // NOT /customers. Creating a customer is almost always the first half of
  // "new lead for someone new", and the second half needs the id the server
  // hasn't issued yet. Queueing the customer alone would sync a contact with
  // no project attached while the rep was told the lead was saved. Offline
  // lead capture therefore works for an existing customer, where the id is
  // already known; a brand-new customer needs a connection and says so.
  // Supporting the full chain offline means client-generated ids, which is a
  // deliberate future change rather than something to half-do here.
  /^\/projects$/,
  /^\/projects\/[^/]+\/site-visit$/,
  /^\/jobs$/,
  /^\/jobs\/[^/]+\/progress$/,
  /^\/jobs\/[^/]+\/(start|complete)$/,
  /^\/proposals$/,
  /^\/proposals\/[^/]+$/,
  /^\/agreements$/,
  /^\/agreements\/[^/]+$/,
  /^\/invoices$/,
  /^\/invoices\/[^/]+$/,
];

export const isQueueable = (path) => QUEUEABLE.some(re => re.test(path.split('?')[0]));

const newKey = () =>
  (crypto.randomUUID?.() ?? `k-${Date.now()}-${Math.random().toString(36).slice(2)}`);

export async function enqueue({ method, path, body }) {
  const db = await withTimeout(dbp);
  const entry = {
    method, path, body,
    idempotencyKey: newKey(),
    at: Date.now(),
    attempts: 0,
  };
  await db.add(QUEUE, entry);
  notify();
  return entry;
}

export async function pendingCount() {
  try {
    const db = await withTimeout(dbp);
    return await db.count(QUEUE);
  } catch { return 0; }
}

export async function pendingItems() {
  try {
    const db = await withTimeout(dbp);
    return await db.getAll(QUEUE);
  } catch { return []; }
}

let flushing = false;

/**
 * Replay the queue oldest-first, stopping at the first network failure so
 * ordering is preserved — a site visit must not be applied before the lead it
 * belongs to exists.
 */
export async function flushQueue(getToken) {
  if (flushing || !navigator.onLine) return { sent: 0, failed: 0 };
  flushing = true;
  let sent = 0, failed = 0;

  try {
    const db = await withTimeout(dbp);
    const items = (await db.getAll(QUEUE)).sort((a, b) => a.at - b.at);

    for (const item of items) {
      try {
        const res = await fetch('/api' + item.path, {
          method: item.method,
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': item.idempotencyKey,
            ...(getToken?.() ? { Authorization: 'Bearer ' + getToken() } : {}),
          },
          body: item.body !== undefined ? JSON.stringify(item.body) : undefined,
        });

        if (res.ok) {
          await db.delete(QUEUE, item.id);
          sent++;
          continue;
        }

        // The server answered and refused. Replaying will not change that, so
        // drop it rather than retrying for ever — but keep it visible.
        if (res.status >= 400 && res.status < 500) {
          const detail = await res.json().catch(() => ({}));
          console.warn('[sync] server rejected a queued change, dropping:', item.path, detail?.error);
          await db.delete(QUEUE, item.id);
          failed++;
          continue;
        }

        // 5xx — server-side and probably transient. Leave it queued.
        await db.put(QUEUE, { ...item, attempts: item.attempts + 1 });
        break;
      } catch {
        // Genuinely offline again. Stop; order matters.
        await db.put(QUEUE, { ...item, attempts: item.attempts + 1 });
        break;
      }
    }
  } catch {
    // dbp itself timed out or rejected — nothing to replay against, same as
    // having found zero items. Callers (SyncStatus's "Sync now") must get a
    // normal return here rather than a rejection, or the button hangs.
  } finally {
    flushing = false;
    notify();
  }
  return { sent, failed };
}

// ------------------------------------------------------------ subscriptions

const listeners = new Set();
export function onQueueChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify() {
  pendingCount().then(n => listeners.forEach(fn => fn(n)));
}

/**
 * Drop Cache Storage buckets left behind by earlier service workers.
 *
 * Workbox's cleanupOutdatedCaches only prunes its own precaches, so the
 * hand-written worker's bucket ('5river-v*') would sit on the device for ever
 * holding a dead copy of the pre-React shell.
 */
async function dropLegacyCaches() {
  if (!('caches' in window)) return;
  try {
    const keys = await caches.keys();
    await Promise.all(
      keys.filter(k => !k.startsWith('workbox-') && k !== 'images')
        .map(k => caches.delete(k)),
    );
  } catch { /* storage pressure or private mode — not worth failing boot over */ }
}

/** Call once at boot. Flushes on reconnect and when the tab regains focus. */
export function startSync(getToken) {
  dropLegacyCaches();
  const go = () => flushQueue(getToken);
  window.addEventListener('online', go);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) go(); });
  go();
  notify();
  return () => window.removeEventListener('online', go);
}
